import { getHistoryData } from "../../data/ramotswe";
import {
  getCurrentObservatoryName,
  changeBinnedTimeSeriesUTCTimeToLocalTime,
  createNullBinnedTimeSeriesData,
  getXLims,
  getBinnedXValues,
  getWidestXLimits,
  ensureYKeyPresence,
  trimNullEdges,
} from "../../utils";
import { DashboardTimeLineGraph } from "../../graphs/base_graphs";
import { DateTime } from "../../imports";

export default async function initializeSolarPower() {
  async function getData() {
    const observatoryName = getCurrentObservatoryName();
    const endDateTime = DateTime.now().setZone("UTC");
    const startDateTime = endDateTime.minus({ months: 18 });
    // hours of sunlight
    let hoursData = await getHistoryData(
      observatoryName,
      "astronomical/sunlight_hours",
      startDateTime,
      endDateTime
    );

    // solar energy
    let energyData = await getHistoryData(
      observatoryName,
      "solar/solar_energy_produced",
      startDateTime,
      endDateTime,
      {
        bin_type: "sum",
      }
    );

    if (energyData === null) {
      energyData = createNullBinnedTimeSeriesData(
        "solar_energy_produced",
        startDateTime,
        endDateTime
      );
    }

    // process data
    hoursData = ensureYKeyPresence(hoursData, ["hours_sunlight"]);
    hoursData = trimNullEdges(hoursData, ["hours_sunlight"]);

    energyData = changeBinnedTimeSeriesUTCTimeToLocalTime(energyData);
    energyData = ensureYKeyPresence(energyData, ["solar_energy_produced"]);
    energyData = trimNullEdges(energyData, ["solar_energy_produced"]);

    // get xlims
    const hoursXlims = getXLims(hoursData.map((h) => h.date_local));
    const energyXlims = getXLims(getBinnedXValues(energyData));
    const xLims = getWidestXLimits([hoursXlims, energyXlims]);

    return [[hoursData, energyData], xLims];
  }

  // set up the initial data and axes
  const [initialData, xLims] = await getData();

  const solarPowerGraph = new DashboardTimeLineGraph(
    "#solar-sunhours-graph",
    "Date",
    "Daily Sunlight [Hours]",
    "Solar Energy Produced [kWh]"
  );

  // set the graph parameters for consistency in initialization & refresh
  function makeBarGraphParams(allGraphData, hideSVG, xLims) {
    return [
      allGraphData[1],
      "time_bin_edges",
      "solar_energy_produced",
      {
        secondAxis: true,
        transition: true,
        staticYlims: [0, 200],
        hideSVG: hideSVG,
        staticXlims: xLims,
      },
    ];
  }
  function makeLineGraphParams(allGraphData, hideSVG, xLims) {
    return [
      allGraphData[0],
      "date_local",
      "hours_sunlight",
      {
        secondAxis: false,
        transition: true,
        staticYlims: [6, 18],
        hideSVG: hideSVG,
        staticXlims: xLims,
      },
    ];
  }

  // populate the graphs, saving the names of them
  const barAxesKey = solarPowerGraph.makeBarGraph(
    ...makeBarGraphParams(initialData, true, xLims)
  );
  const lineAxesKey = solarPowerGraph.makeLineGraph(
    ...makeLineGraphParams(initialData, true, xLims)
  );

  solarPowerGraph.showLoading();

  // define the refresh function
  async function refresh() {
    solarPowerGraph.showLoading();
    const [newData, xLims] = await getData();

    // updateData has the same format as make*Graph, but with the key as param 1
    solarPowerGraph.updateData(
      barAxesKey,
      ...makeBarGraphParams(newData, false, xLims)
    );
    solarPowerGraph.updateData(
      lineAxesKey,
      ...makeLineGraphParams(newData, false, xLims)
    );
  }

  return refresh;
}
